import { formatAccounting } from "helpers/formatNumber";
import { IFRCalculationResult } from "models/HaulPay";
import styles from "./style.module.scss";
import clsx from "clsx";

interface ISharedValue {
  frCalculation: IFRCalculationResult;
  isMarginAdvance?: boolean;
}

export default function FrResult({
  frCalculation,
  isMarginAdvance = false
}: Readonly<ISharedValue>) {
  const {
    contract_type: contractType,
    payment_profile_discount_rate: carrierRate,
    total_comfreight_fee: totalFee,
    payment_profile_fee: paymentProfileFee,
    factoring_fee: yourFee,
    payment_profile_payable: carrierPay,
    factoring_payable: yourPay,
    carrier_total_amount: carrierTotalAmount,
    self_finance_total_amount: debitAmount,
    broker_self_finance_fee: selfFinanceFee,
    self_finance_fee_rate_info: selfFinanceFeeRateInfo,
    discount_rate_comfreight: QPRate,
    reserve_amount: reserveAmount,
    factoring_fee_split_rate: carrierQPRate,
    carrier_line_haul: carrierLineHaul,
    broker_carrier_advance: brokerLineHaul,
    qp_total_fee: QPTotalFee,
    carrier_qp_fee: carrierQPFee,
    debtor_additional_discount_rate: debtorAdditionalDiscountRate,
    broker_additional_discount_fee_for_carrier: brokerAdditionalDiscountFeeForCarrier,
    broker_additional_discount_fee_for_broker: brokerAdditionalDiscountFeeForBroker,
    margin_advance_fee: marginAdvanceFee,
    broker_discount_fee_for_carrier: brokerDiscountFeeForCarrier,
    invoice_amount: invoiceAmount,
    payment_profile_invoice_amount: carrierInvoiceAmount
  } = frCalculation;

  const hasDebtorAdditionalRate = (debtorAdditionalDiscountRate ?? 0) > 0;
  const netQPProfit = (brokerLineHaul ?? 0) - (debitAmount ?? 0);
  const brokerGrossMargin = (invoiceAmount ?? 0) - (carrierInvoiceAmount ?? 0);

  return (
    <div>
      {contractType.startsWith("SELF_FINANCE") ? (
        <div className={styles.defaultPayment}>
          <div className={styles.financeColumn}>
            <span className={styles.columnHeader}>Carrier</span>
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>Line Haul</span>
              <span className={styles.value}>
                {formatAccounting((carrierLineHaul ?? 0) / 100, 2)}
              </span>
            </div>
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>QP Rate {QPRate}%</span>
              <span className={styles.debitValue}>
                {formatAccounting(-((QPTotalFee ?? 0) / 100), 2)}
              </span>
            </div>
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>
                Carrier QP Fee {100 - (carrierQPRate ?? 0)}%
              </span>
              <span className={styles.debitValue}>
                {formatAccounting(-((carrierQPFee ?? 0) / 100), 2)}
              </span>
            </div>
            <div className={clsx(styles.lineItem, styles.totalRow)}>
              <span className={styles.label}>Carrier Pay</span>
              <span className={styles.creditValue}>
                {formatAccounting((carrierTotalAmount ?? 0) / 100, 2)}
              </span>
            </div>
          </div>
          <div className={styles.financeColumn}>
            <span className={styles.columnHeader}>Broker</span>
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>Line Haul</span>
              <span className={styles.value}>
                {formatAccounting((brokerLineHaul ?? 0) / 100, 2)}
              </span>
            </div>
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>
                Self Finance Fee
                <span className={styles.feeMeta}>({selfFinanceFeeRateInfo ?? "Flat"})</span>
              </span>
              <span className={styles.debitValue}>
                {formatAccounting(-((selfFinanceFee ?? 0) / 100), 2)}
              </span>
            </div>
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>Gross QP Profit</span>
              <span className={styles.value}>
                {formatAccounting((paymentProfileFee ?? 0) / 100, 2)}
              </span>
            </div>
            <div className={clsx(styles.lineItem, styles.subtotalRow)}>
              <span className={styles.labelLineItem}>Debit Amount</span>
              <span className={styles.debitSubtotal}>
                {formatAccounting(-((debitAmount ?? 0) / 100), 2)}
              </span>
            </div>
            <div className={clsx(styles.lineItem, styles.totalRow)}>
              <span className={styles.label}>Net QP Profit</span>
              <span className={netQPProfit >= 0 ? styles.creditValue : styles.debitValue}>
                {formatAccounting(netQPProfit / 100, 2)}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.defaultPayment}>
          {/* Carrier Column - Show for all except NON_FACTORED_BILL_OUT */}
          {contractType !== "NON_FACTORED_BILL_OUT" && (
            <div className={styles.financeColumn}>
              <div className={clsx(styles.lineItem, styles.feeRow)}>
                <span className={styles.label}>Total Fee</span>
                <span className={styles.debitValue}>{formatAccounting(-totalFee / 100, 2)}</span>
              </div>
              {contractType !== "NON_FACTORED_STANDARD" && (
                <div className={styles.lineItem}>
                  <span className={styles.labelLineItem}>Carrier Invoice Amount</span>
                  <span className={styles.value}>
                    {formatAccounting((carrierInvoiceAmount ?? 0) / 100, 2)}
                  </span>
                </div>
              )}
              <div className={styles.lineItem}>
                <span className={styles.labelLineItem}>Carrier&apos;s Rate</span>
                <span className={styles.value}>
                  {contractType !== "NON_FACTORED_STANDARD" ? `${carrierRate}%` : "0%"}
                </span>
              </div>
              <div className={styles.lineItem}>
                <span className={styles.label}>Carrier Fee</span>
                <span className={styles.debitValue}>
                  {contractType !== "NON_FACTORED_STANDARD"
                    ? formatAccounting(-paymentProfileFee / 100, 2)
                    : "$0.00"}
                </span>
              </div>
              <div className={clsx(styles.lineItem, styles.totalRow)}>
                <span className={styles.label}>Carrier&apos;s Pay</span>
                <span className={styles.creditValue}>{formatAccounting(carrierPay / 100, 2)}</span>
              </div>
            </div>
          )}
          {/* Broker Column */}
          <div className={styles.financeColumn}>
            {contractType === "NON_FACTORED_BILL_OUT" && (
              <div className={styles.lineItem}>
                <span className={styles.label}>Total Fee</span>
                <span className={styles.debitValue}>{formatAccounting(-totalFee / 100, 2)}</span>
              </div>
            )}
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>Broker Gross Margin</span>
              <span className={styles.value}>{formatAccounting(brokerGrossMargin / 100, 2)}</span>
            </div>
            {/* Carrier Payment Fee Paid by Broker - show for Factored types */}
            {contractType !== "NON_FACTORED_BILL_OUT" &&
              contractType !== "NON_FACTORED_STANDARD" &&
              (brokerDiscountFeeForCarrier ?? 0) > 0 && (
                <div className={styles.lineItem}>
                  <span className={styles.labelLineItem}>Carrier Payment Fee Paid by Broker</span>
                  <span className={styles.debitValue}>
                    {formatAccounting(-(brokerDiscountFeeForCarrier ?? 0) / 100, 2)}
                  </span>
                </div>
              )}
            {/* Debtor Additional Discount (Carrier) */}
            {hasDebtorAdditionalRate && (brokerAdditionalDiscountFeeForCarrier ?? 0) > 0 && (
              <div className={styles.lineItem}>
                <span className={styles.labelLineItem}>Debtor Additional Discount (Carrier)</span>
                <span className={styles.debitValue}>
                  {formatAccounting(-(brokerAdditionalDiscountFeeForCarrier ?? 0) / 100, 2)}
                </span>
              </div>
            )}
            {/* Margin Advance Fee - show when margin advance is enabled */}
            {isMarginAdvance && (marginAdvanceFee ?? 0) > 0 && (
              <div className={styles.lineItem}>
                <span className={styles.labelLineItem}>Margin Advance Fee</span>
                <span className={styles.debitValue}>
                  {formatAccounting(-(marginAdvanceFee ?? 0) / 100, 2)}
                </span>
              </div>
            )}
            {/* Debtor Additional Discount (Margin Adv.) */}
            {isMarginAdvance &&
              hasDebtorAdditionalRate &&
              (brokerAdditionalDiscountFeeForBroker ?? 0) > 0 && (
                <div className={styles.lineItem}>
                  <span className={styles.labelLineItem}>
                    Debtor Additional Discount (Margin Adv.)
                  </span>
                  <span className={styles.debitValue}>
                    {formatAccounting(-(brokerAdditionalDiscountFeeForBroker ?? 0) / 100, 2)}
                  </span>
                </div>
              )}
            <div className={styles.lineItem}>
              <span className={styles.labelLineItem}>Your Fee</span>
              <span className={styles.debitValue}>{formatAccounting(-yourFee / 100, 2)}</span>
            </div>
            <div className={clsx(styles.lineItem, styles.totalRow)}>
              <span className={styles.label}>Your Pay</span>
              <span className={styles.creditValue}>{formatAccounting(yourPay / 100, 2)}</span>
            </div>
            {/* Reserve Holdback - show when margin advance is enabled */}
            {isMarginAdvance && (reserveAmount ?? 0) > 0 && (
              <div className={styles.lineItem}>
                <span className={styles.labelLineItem}>Reserve Holdback</span>
                <span className={styles.value}>
                  {formatAccounting((reserveAmount ?? 0) / 100, 2)}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
